import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle, Clock, ExternalLink, ArrowLeft, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";

export default function BranchDetail() {
  const { slug } = useParams();
  const { t, lang, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [branch, setBranch] = useState(null);

  useEffect(() => {
    api.get(`/branches/${slug}`).then((r) => setBranch(r.data)).catch(() => setBranch(null));
  }, [slug]);

  if (!branch) return <div className="py-32 text-center text-slate-500">Loading...</div>;

  return (
    <div data-testid="branch-detail-page">
      <section className="relative aspect-[21/9] min-h-[340px] overflow-hidden">
        <img src={branch.image} alt={branch.name_en} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-10">
            <Link to="/branches" data-testid="branch-back" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-3">
              <Arrow className="w-4 h-4 rotate-180" /> {t.branches.backToAll}
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white" data-testid="branch-name">
              {lang === "ar" ? branch.name_ar : branch.name_en}
            </h1>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            {(branch.description_en || branch.description_ar) && (
              <p className="text-slate-700 text-lg leading-relaxed">
                {lang === "ar" ? branch.description_ar : branch.description_en}
              </p>
            )}

            {branch.lat && branch.lng && (
              <div className="rounded-lg overflow-hidden border border-slate-200 aspect-[16/9]">
                <iframe
                  title={`map-${branch.id}`}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${branch.lng - 0.02}%2C${branch.lat - 0.02}%2C${branch.lng + 0.02}%2C${branch.lat + 0.02}&layer=mapnik&marker=${branch.lat}%2C${branch.lng}`}
                  className="w-full h-full"
                  loading="lazy"
                />
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="bg-slate-50 rounded-lg p-6 space-y-4">
              <InfoRow icon={MapPin} label={t.branches.address} value={lang === "ar" ? branch.address_ar : branch.address_en} />
              {branch.working_hours_en && (
                <InfoRow icon={Clock} label={t.branches.workingHours} value={lang === "ar" ? branch.working_hours_ar : branch.working_hours_en} />
              )}
              {branch.phones?.length > 0 && (
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#0891b2] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase text-slate-500 tracking-widest mb-1">{t.branches.phone}</div>
                    <div className="space-y-1">
                      {branch.phones.map((p, i) => (
                        <a key={i} href={`tel:${p}`} className="block text-slate-800 hover:text-[#0891b2]" dir="ltr">{p}</a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {branch.email && <InfoRow icon={Mail} label={t.branches.email} value={<a href={`mailto:${branch.email}`} dir="ltr" className="hover:text-[#0891b2]">{branch.email}</a>} />}
              {branch.whatsapp && <InfoRow icon={MessageCircle} label={t.branches.whatsapp} value={<a href={`https://wa.me/${branch.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" dir="ltr" className="text-green-700">{branch.whatsapp}</a>} />}
            </div>

            {branch.map_url && (
              <a href={branch.map_url} target="_blank" rel="noreferrer" data-testid="branch-map-open">
                <Button className="w-full bg-[#0891b2] hover:bg-[#ef4444] text-white h-11">
                  <ExternalLink className="w-4 h-4 me-2" /> {t.branches.viewOnMap}
                </Button>
              </a>
            )}
            {branch.whatsapp && (
              <a href={`https://wa.me/${branch.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white h-11">
                  <MessageCircle className="w-4 h-4 me-2" /> WhatsApp
                </Button>
              </a>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-[#0891b2] mt-0.5 shrink-0" />
      <div>
        <div className="text-[11px] uppercase text-slate-500 tracking-widest mb-1">{label}</div>
        <div className="text-sm text-slate-800">{value}</div>
      </div>
    </div>
  );
}
